import type { VinDetailEntity } from '../../../vin/entities';
import type { TechlineCaseEntity } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import {
    deduplicateRetailerProfiles,
    normalizeCaseDetailsResponse,
    toRetailerProfileEntity,
    toTechlineCaseEntity,
    toVinDetailEntity,
} from '../helpers';

/** Parameters for fetching case details. */
export type FetchCaseDetailsParams = {
    /** The case number to fetch details for. */
    caseNumber: string;
};

/** The result of fetching case details, containing both case and VIN data. */
export type FetchCaseDetailsResult = {
    /** The case details with deduplicated retailer profiles. */
    caseDetails: TechlineCaseEntity;

    /** The first VIN detail entry, or undefined if none exists. */
    vinDetails: VinDetailEntity | undefined;
};

/**
 * Fetch full case details from NorthStar, deduplicate retailer profiles,
 * and extract VIN details. Returns both the normalized case data and the
 * first VIN detail entry for the associated vehicle.
 */
export class FetchCaseDetails extends StsBackendOperation {
    /**
     * Fetch and normalize case details for a given case number.
     *
     * @param params - the case to fetch details for
     * @returns the case details and VIN details, or undefined if the API call failed
     */
    async exec(params: FetchCaseDetailsParams): Promise<FetchCaseDetailsResult | undefined> {
        const { caseNumber } = params;
        // Fetch the raw case details from the API
        const response = await this.client.getCaseDetails({ caseNumber });

        if (!response?.success || !response.data) {
            return undefined;
        }

        // Normalize the polymorphic caseDetails shape
        const rawDetails = normalizeCaseDetailsResponse(response.data.caseDetails);

        // Deduplicate retailer profiles and attach as retailerUniqueProfiles
        const uniqueProfiles = deduplicateRetailerProfiles(rawDetails.retailerProfiles.map(toRetailerProfileEntity));
        const caseDetails = toTechlineCaseEntity({ ...rawDetails, retailerUniqueProfiles: uniqueProfiles });

        // Extract the first VIN detail entry, if available
        const vinDetails = response.data.vinDetails?.[0] ? toVinDetailEntity(response.data.vinDetails[0]) : undefined;

        return { caseDetails, vinDetails };
    }
}
